import { CaretRightOutlined } from '@ant-design/icons';
import { useDraggable } from '@dnd-kit/core';
import { Tabs, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomElement } from '@/api/customElements';

import type { BuilderDragData } from './builderDnd';
import { CatalogTabContent, type CatalogFieldDefinition } from './customElements/CatalogTabContent';
import { CustomElementTabContent } from './customElements/CustomElementTabContent';
import { PALETTE_CATEGORIES, type PaletteEntry } from './fieldMeta';

const { Text } = Typography;

type Props = {
  selectedEntry: PaletteEntry | null;
  onSelect: (entry: PaletteEntry | null) => void;
  /** Inserts a fresh copy of a saved Section custom element at the end of the canvas. */
  onPickCustomSection: (el: CustomElement) => void;
  /** When true, hides Section cards from the Custom tab (used when editing a Section element). */
  hideSections?: boolean;
};

const RAIL: CSSProperties = {
  width: 296,
  flexShrink: 0,
  borderRight: '1px solid #e5e7eb',
  background: '#fff',
  padding: 16,
  overflowY: 'auto',
};

const RAIL_HEADER: CSSProperties = {
  paddingBottom: 14,
  borderBottom: '1px solid #f1f5f9',
  marginBottom: 14,
};

const CATEGORY_HEADER_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  padding: '4px 2px',
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  textAlign: 'left',
};

const CARD_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fff',
  textAlign: 'left',
  width: '100%',
  cursor: 'pointer',
  transition: 'border-color 140ms ease, background 140ms ease, box-shadow 140ms ease',
};

const ICON_TILE: CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 7,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 15,
  flexShrink: 0,
};

/**
 * Left rail of the builder. Categorized cards per spec §6.2 — each category is
 * a collapsible group with a chevron header. Click-to-arm: the selected card
 * stays highlighted until the user clicks an empty column on the canvas. Top
 * of the rail carries the builder's name + one-line description so the page
 * intent is clear without consulting the page header.
 */
export function FieldPalette({
  selectedEntry,
  onSelect,
  onPickCustomSection,
  hideSections = false,
}: Props) {
  const { t } = useTranslation();
  // All categories start expanded — the spec mock shows them collapsible so
  // the originator can hide the ones they're not using right now.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (id: string) => setCollapsed((cur) => ({ ...cur, [id]: !cur[id] }));

  const armedCustomFieldId = selectedEntry?.kind === 'customField' ? selectedEntry.id : null;

  return (
    <aside style={RAIL}>
      <div style={RAIL_HEADER}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 6 }}>
          {t('workflow.builder.palette.title')}
        </Text>
        <Text style={{ fontSize: 12, lineHeight: 1.45, color: '#475569' }}>
          {t('workflow.builder.palette.description')}
        </Text>
      </div>

      <Tabs
        size="small"
        items={[
          {
            key: 'catalog',
            label: t('workflow.builder.palette.catalogTab'),
            children: (
              <CatalogTabContent
                armedFieldId={armedCustomFieldId}
                onPickField={(entry: CatalogFieldDefinition) => {
                  const catalogEntry: PaletteEntry = {
                    kind: 'customField',
                    id: entry.id,
                    type: entry.type,
                    defaultLabelEn: entry.labelEn,
                    defaultLabelFr: entry.labelFr,
                    defaultRequired: entry.required,
                    defaultProperties: () => entry.properties,
                  };
                  onSelect(armedCustomFieldId === entry.id ? null : catalogEntry);
                }}
              />
            ),
          },
          {
            key: 'palette',
            label: t('workflow.builder.palette.paletteTab'),
            children: (
              <>
                {PALETTE_CATEGORIES.map((cat) => {
                  const isCollapsed = collapsed[cat.id] ?? false;
                  return (
                    <div key={cat.id} style={{ marginBottom: 12 }}>
                      <button
                        type="button"
                        style={CATEGORY_HEADER_BTN}
                        onClick={() => toggle(cat.id)}
                      >
                        <CaretRightOutlined
                          style={{
                            fontSize: 10,
                            transition: 'transform 140ms ease',
                            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                          }}
                        />
                        {t(cat.labelKey)}
                      </button>
                      {!isCollapsed && (
                        <div
                          style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}
                        >
                          {cat.entries.map((entry) => {
                            const armed = selectedEntry?.id === entry.id;
                            return (
                              <PaletteCard
                                key={entry.id}
                                entry={entry}
                                armed={armed}
                                onClick={() => onSelect(armed ? null : entry)}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {selectedEntry && (
                  <Text
                    type="secondary"
                    style={{ display: 'block', marginTop: 12, fontSize: 12, padding: '0 4px' }}
                  >
                    {t('workflow.builder.palette.armedHint')}
                  </Text>
                )}
              </>
            ),
          },
          {
            key: 'custom',
            label: t('workflow.customElements.tabLabel'),
            children: (
              <CustomElementTabContent
                types={hideSections ? ['Field'] : ['Field', 'Section']}
                armedFieldId={armedCustomFieldId}
                onPickField={(el) => {
                  if (!el.field) return;
                  const customEntry: PaletteEntry = {
                    kind: 'customField',
                    id: el.id,
                    type: el.field.type,
                    defaultLabelEn: el.field.labelEn,
                    defaultLabelFr: el.field.labelFr || null,
                    defaultRequired: el.field.required,
                    defaultProperties: () => el.field!,
                  };
                  onSelect(armedCustomFieldId === el.id ? null : customEntry);
                }}
                onPickSection={onPickCustomSection}
              />
            ),
          },
        ]}
      />
    </aside>
  );
}

function PaletteCard({
  entry,
  armed,
  onClick,
}: {
  entry: Extract<PaletteEntry, { kind: 'field' | 'compound' }>;
  armed: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  // Compounds create a whole section rather than filling one column slot, so
  // there is nothing for them to be dropped ONTO — they stay click-only.
  const draggable = entry.kind === 'field';
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${entry.id}`,
    data: draggable ? ({ kind: 'palette', entry } satisfies BuilderDragData) : undefined,
    disabled: !draggable,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      // Spread order matters: dnd-kit's listeners must not clobber onClick, and
      // its `attributes` carry the keyboard/ARIA wiring. The PointerSensor's
      // activation distance is what keeps a plain press a click — see
      // DRAG_ACTIVATION_DISTANCE_PX.
      {...attributes}
      {...listeners}
      style={{
        ...CARD_BASE,
        background: armed ? entry.tintBg : '#fff',
        borderColor: armed ? entry.tintFg : '#e5e7eb',
        boxShadow: armed
          ? `0 2px 6px ${entry.tintBg}, 0 0 0 1px ${entry.tintFg}`
          : '0 1px 2px rgba(0, 0, 0, 0.03)',
        cursor: draggable ? 'grab' : 'pointer',
        // The card stays put; the DragOverlay is what follows the cursor.
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <span style={{ ...ICON_TILE, background: entry.tintBg, color: entry.tintFg }}>
        {entry.icon()}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ fontSize: 13, color: '#111827', display: 'block' }}>
          {t(entry.labelKey)}
        </Text>
        <Text style={{ fontSize: 11, lineHeight: 1.35, color: '#475569' }}>
          {t(entry.descriptionKey)}
        </Text>
      </div>
    </button>
  );
}
